import { AuthorLabel } from '~/components/Author'
import { StructuredSchema } from '~/components/StructuredSchema'
import { generatePath } from 'react-router'
import { routes } from '~/queries/routes'
import { useRootContext } from '~/hooks/useRootContext'

import { Link } from '@remix-run/react'
import classNames from 'classnames'
import { Button } from '~/components/general/Button'
import { trackEvent } from '~/utils/tracking'
import { ResponsiveImage } from '~/components/ResponsiveImage'
import {
  TypeArticle,
  TypeAuthorSkeleton,
  TypeBlogCategorySkeleton,
  TypeIllustratorSkeleton,
} from '~/types/contentful'
import { Asset, Entry } from 'contentful'

export type ArticleHeroProps = {
  data: TypeArticle<'WITHOUT_UNRESOLVABLE_LINKS', string>
}

export const ArticleHero = ({ data }: ArticleHeroProps) => {
  const {
    url: { origin },
  } = useRootContext()
  const { color, author, illustration, illustrationAlt, category, title, illustrator } = data.fields

  const onClickCta = () => {
    trackEvent('click_blog_cta', {
      block: 'hero',
    })
  }

  const categoryEntry = category as Entry<
    TypeBlogCategorySkeleton,
    'WITHOUT_UNRESOLVABLE_LINKS',
    string
  >
  const illustrationImage = illustration as Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>
  const illustratorEntry = illustrator as Entry<
    TypeIllustratorSkeleton,
    'WITHOUT_UNRESOLVABLE_LINKS',
    string
  >

  return (
    <div
      className={classNames(
        'relative bg-neutral-5 text-neutral-100 rounded-[40px] mb-15 xl:pt-9 max-md:pt-5 max-md:px-3 max-md:pb-11 max-md:mt-0 max-md:-mx-3 max-md:mb-2  max-lg:pt-6 max-lg:px-6 max-lg:pb-14 xl:px-9 xl:pb-13 max-lg:flex-col max-lg:flex max-xl:pb-10 max-xl:mb-5 max-xl:rounded-3xl',
      )}
      style={{ backgroundColor: color }}
    >
      <StructuredSchema
        schema={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Career Advice',
              item: `${origin}${generatePath(routes.careerAdvice)}`,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: categoryEntry?.fields.name,
              item: `${origin}${generatePath(routes.article, {
                slug: categoryEntry?.fields?.slug || '',
              })}`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: title,
            },
          ],
        }}
      />

      <ol className="w-[40%] text-neutral-60 overflow-hidden p-0 flex mb-40 max-xl:mb-44 max-lg:pb-5 max-lg:mb-0 max-lg:w-[80%] max-md:mb-4 max-md:p-0 max-md:w-full">
        <li
          className={classNames(
            'max-lg:text-[17px] max-md:text-[16px] text-neutral-80 after:invert-[90%] after:bg-no-repeat after:-translate-y-1/2 after:[background-size:6px_16px] after:bg-[url("/images/breadcrumb-chevron.svg")] after:top-1/2 after:right-0 after:absolute after:block after:w-4 after:h-4 after:content-[""] last:pr-0 last:flex last:after:hidden pr-6 relative whitespace-nowrap overflow-hidden text-ellipsis text-[19px]',
          )}
        >
          <Link
            className={classNames('cursor-pointer hover:text-neutral-90')}
            to={generatePath(routes.careerAdvice)}
            title="Career Advice"
          >
            Career Advice
          </Link>
        </li>
        {category && (
          <li
            className={classNames(
              'max-lg:text-[17px] text-neutral-80 after:invert-[90%] max-md:text-[16px] after:bg-no-repeat after:-translate-y-1/2 after:[background-size:6px_16px] after:bg-[url("/images/breadcrumb-chevron.svg")] after:top-1/2 after:right-0 after:content-[""] after:absolute after:block after:w-4 after:h-4 last:pr-0 last:flex last:after:hidden pr-4 block relative whitespace-nowrap overflow-hidden text-ellipsis text-[19px]',
            )}
          >
            <Link
              className={classNames('cursor-pointer hover:text-neutral-90')}
              to={generatePath(routes.article, {
                slug: categoryEntry.fields?.slug || '',
              })}
              title={categoryEntry.fields.name}
            >
              {categoryEntry.fields.name}
            </Link>
          </li>
        )}
        <li
          className={classNames(
            'text-neutral-80 after:invert-[90%] max-lg:text-[17px] max-md:text-[16px] after:bg-no-repeat after:-translate-y-1/2 after:[background-size:6px_16px] after:bg-[url("/images/breadcrumb-chevron.svg")] after:top-1/2 after:right-0 after:content-[""] after:absolute after:block after:w-4 after:h-4 last:pr-0 last:flex last:after:hidden pr-4 block relative whitespace-nowrap overflow-hidden text-ellipsis text-[19px]',
          )}
        >
          <span title={title}>{title}</span>
        </li>
      </ol>

      <div className="absolute top-1/2 left-[46%] max-md:m-0 max-md:mb-5 max-md:w-[calc(100%_-_16px)] max-2xl:left-[48.5%] max-xl:w-[49.2%] -translate-y-1/2 w-[580px] max-xl:max-w-[512px] max-lg:static max-lg:transform-none max-lg:self-center max-lg:h-auto max-lg:w-[62.5%] max-lg:mt-0 max-lg:mx-0 max-lg:-mb-8">
        {illustrationImage && (
          <div className="w-full flex justify-center object-contain">
            <ResponsiveImage
              asset={illustrationImage.fields}
              alt={illustrationAlt}
              opts={{ width: 580 }}
              fetchPriority="high"
            />
          </div>
        )}
      </div>
      <div className="lg:w-[45.56%] xl:w-[40.56%]">
        <div className="mb-6 w-[41%] max-2xl:w-[43%] max-lg:pr-5 max-lg:w-[37.5%] max-md:w-full max-md:p-0">
          {author && (
            <AuthorLabel
              fields={
                (author as Entry<TypeAuthorSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS', string>).fields
              }
            />
          )}
        </div>
        <h1 className="text-2xl">{title}</h1>
        {categoryEntry?.fields?.ctaText && categoryEntry?.fields?.ctaBannerLink && (
          <Button
            type={'link'}
            className="mt-6"
            onClick={onClickCta}
            href={categoryEntry.fields.ctaBannerLink}
            text={categoryEntry.fields.ctaText}
          />
        )}
      </div>

      {illustratorEntry && (
        <p className="absolute bottom-3 right-14 opacity-30 max-xl:bottom-2 max-xl:right-8 max-lg:bottom-3 max-lg:right-8 max-md:right-7">
          Artwork by:&nbsp;
          {illustratorEntry.fields.socialProfile ? (
            <a
              className="underline hover:no-underline"
              href={illustratorEntry.fields.socialProfile}
              target="_blank"
              rel="noreferrer"
            >
              {illustratorEntry.fields.name}
            </a>
          ) : (
            <u className="underline">{illustratorEntry.fields.name}</u>
          )}
        </p>
      )}
    </div>
  )
}
